import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { userService } from '../../../_services';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import DateRangePicker from '../../ui/date-range-picker';
import Pagination from '../../ui/pagination';
import { Loader2, Search, ArrowLeft, Receipt, ExternalLink } from 'lucide-react';

/**
 * NLS Rx Marketplace Orders Report
 * Modernized admin report for viewing NLS marketplace orders
 */
function NLSRxOrdersReport({ hideNLSRxSection }) {
    // Date range state
    const defaultStartDate = moment('01-30-2025').toDate();
    const defaultEndDate = moment().toDate();

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [ordersList, setOrdersList] = useState([]);
    const [ordersListTemp, setOrdersListTemp] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);

    // Dialog state
    const [showOrderDialog, setShowOrderDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Fetch orders on mount and date change
    useEffect(() => {
        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        fetchOrders(userId, moment(startDate).format('MM/DD/YYYY'), moment(endDate).format('MM/DD/YYYY'));
    }, []);

    const fetchOrders = async (userId, start, end) => {
        setLoading(true);
        try {
            const startFormatted = moment(start, 'MM/DD/YYYY').format('YYYY-MM-DD');
            const endFormatted = moment(end, 'MM/DD/YYYY').format('YYYY-MM-DD');

            const apidata = await userService.FetchNLSPaymentsAdmin(userId, startFormatted, endFormatted);

            if (apidata && apidata.length > 0) {
                setOrdersList(apidata);
                setOrdersListTemp(apidata);
            } else {
                setOrdersList([]);
                setOrdersListTemp([]);
            }
        } catch (err) {
            console.error('Error fetching NLS orders:', err);
            setOrdersList([]);
            setOrdersListTemp([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle date range change
    const handleDateChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
        setSearchText('');

        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        fetchOrders(userId, moment(start).format('MM/DD/YYYY'), moment(end).format('MM/DD/YYYY'));
    };

    // Handle search filter
    const handleSearch = (value) => {
        setSearchText(value);

        if (!value || value.length === 0) {
            setOrdersList(ordersListTemp);
            return;
        }

        const searchLower = value.toLowerCase();
        const filtered = ordersListTemp.filter(order => {
            return (
                (order.email && order.email.toLowerCase().includes(searchLower)) ||
                (order.fullName && order.fullName.toLowerCase().includes(searchLower)) ||
                (order.userName && order.userName.toLowerCase().includes(searchLower)) ||
                (order.phone && order.phone.toLowerCase().includes(searchLower)) ||
                (order.orderStatus && order.orderStatus.toLowerCase().includes(searchLower))
            );
        });

        setOrdersList(filtered.length > 0 ? filtered : []);
    };

    // Handle order details popup
    const handleShowOrderDetails = async (order) => {
        setSelectedOrder(order);
        setShowOrderDialog(true);
        setLoadingDetails(true);

        if (order.orderId && order.orderId !== '-') {
            try {
                const apidata = await userService.FetchNLSPaymentsOrderAdmin(order.orderId);
                setOrderDetails(apidata || []);
            } catch (err) {
                console.error('Error fetching order details:', err);
                setOrderDetails([]);
            } finally {
                setLoadingDetails(false);
            }
        } else if (order.orderId === '-') {
            // Handle error status
            try {
                const statusData = JSON.parse(order.orderStatus);
                setOrderDetails(statusData);
            } catch (err) {
                setOrderDetails([]);
            }
            setLoadingDetails(false);
        } else {
            setOrderDetails([]);
            setLoadingDetails(false);
        }
    };

    // Get status badge variant
    const getStatusVariant = (status) => {
        if (!status) return 'default';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('success') || statusLower.includes('completed')) return 'default';
        if (statusLower.includes('pending')) return 'secondary';
        if (statusLower.includes('failed') || statusLower.includes('error')) return 'destructive';
        return 'default';
    };

    return (
        <div className="w-full space-y-4">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={hideNLSRxSection}
                    className="text-brand-dark hover:text-brand-accent"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <h2 className="text-2xl font-semibold text-brand-dark">NLS Rx Marketplace Orders</h2>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-brand-accent" />}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                            <Input
                                type="text"
                                placeholder="Search by name, email, phone, or status..."
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                disabled={loading}
                                className="pl-9"
                            />
                        </div>

                        {/* Date Range */}
                        <DateRangePicker
                            initialStartDate={startDate}
                            initialEndDate={endDate}
                            onApply={handleDateChange}
                            displayMode="unified"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Orders Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-brand-light-1">
                                    <TableHead className="text-brand-dark font-semibold">User Name</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Full Name</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Email</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Phone</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Order Date</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Order Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8">
                                            <div className="flex items-center justify-center gap-2 text-brand-dark/70">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Records are loading...
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : ordersList.length > 0 ? (
                                    <Pagination items={ordersList} pageSize={10}>
                                        {(paginatedItems) =>
                                            paginatedItems.map((order, index) => (
                                                <TableRow
                                                    key={index}
                                                    className="cursor-pointer hover:bg-brand-light-1"
                                                    onClick={() => handleShowOrderDetails(order)}
                                                >
                                                    <TableCell className="text-brand-dark">{order.userName || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{order.fullName || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{order.email || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{order.phone || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">
                                                        {order.orderDate ? moment(order.orderDate).format('MM/DD/YYYY') : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={getStatusVariant(order.orderStatus)}>
                                                            {order.orderStatus || 'Unknown'}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </Pagination>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-brand-dark/70">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Order Details Dialog */}
            <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle className="text-brand-dark">
                            {selectedOrder?.userName || 'Order Details'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="max-h-[400px] overflow-y-auto">
                        {loadingDetails ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-brand-accent" />
                            </div>
                        ) : selectedOrder?.orderId && selectedOrder.orderId !== '-' ? (
                            orderDetails.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product Name</TableHead>
                                            <TableHead>Quantity</TableHead>
                                            <TableHead>Price</TableHead>
                                            <TableHead>Receipt</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orderDetails.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="text-brand-dark">{item.productName || '-'}</TableCell>
                                                <TableCell className="text-brand-dark">{item.productQuantity || '-'}</TableCell>
                                                <TableCell className="text-brand-dark">{item.productPrice || '-'}</TableCell>
                                                <TableCell>
                                                    {item.paymentReceipt && index === 0 ? (
                                                        <a
                                                            href={item.paymentReceipt}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-brand-accent hover:text-brand-accent/80"
                                                        >
                                                            <Receipt className="h-4 w-4" />
                                                            <span className="text-sm">View Receipt</span>
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="text-center py-8 text-brand-dark/70">No details found</div>
                            )
                        ) : selectedOrder?.orderId === '-' && orderDetails?.errors ? (
                            <div className="space-y-2">
                                {orderDetails.errors.map((error, index) =>
                                    Object.entries(error).map(([key, value]) => (
                                        <div key={`${index}-${key}`} className="grid grid-cols-2 gap-4 p-2 border-b">
                                            <div className="font-medium text-brand-dark">{key}</div>
                                            <div className="text-brand-dark">{value}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-brand-dark/70">No details available</div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default NLSRxOrdersReport;
